import { DO_NOT_USE_OR_YOU_WILL_BE_FIRED_EXPERIMENTAL_CREATE_ROOT_CONTAINERS } from 'react-dom/client';
import { UserStruct, UserTypes, UserRoles, UserTypeInfo, UserRoleInfo } from '../api/auth';

import { messaging } from "../../src/helpers/firebase-config";
import { getToken, deleteToken, onMessage } from "firebase/messaging";
import { v4 as uuidv4 } from 'uuid';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { MemberRole, WpStatus } from '../redux/workplace/wpTypes';
import { setDraftPost } from '../redux/workplace/wpActions';
import { APIClient } from 'api/apiCore';
import * as urls from "api/urls";
import { AppDispatch } from 'redux/store';
import { Utils } from './utils';
import { WpModes } from '../constants';
import { WpPostInfoMem } from './wpPostInfoMem';


const DB_NAME = "chat.2s";
const DB_VERSION = 1;
const STORE_NAME = "store1";

class DbUtils {

  private static async OpenDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // No keyPath → you can provide keys manually
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  public static async PutDbData(key: IDBValidKey, data: any): Promise<number> {
    const db = await this.OpenDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      // const request = store.add(data);
      // const request = store.add(data, key);
      const request = store.put(data, key); // insert or update

      request.onsuccess = () => resolve(request.result as number);
      request.onerror = () => reject(request.error);
    });
  }

  public static async GetDbData(key: IDBValidKey): Promise<any> {
    const db = await this.OpenDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  public static async GetDbDataByPrefix(prefix: string): Promise<any[]> {
    const db = await this.OpenDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);

      // Create a key range to fetch items that start with "push."
      const keyRange = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
      const request = store.openCursor(keyRange);
      const results: any[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  public static async DeleteDbDataByPrefix(prefix: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME);

      request.onerror = (event) => {
        console.error("IndexedDB error:", event);
        reject(event);
      };

      request.onsuccess = (event) => {
        const db = request.result;
        const transaction = db.transaction(STORE_NAME, "readwrite");
        const objectStore = transaction.objectStore(STORE_NAME);

        // Open a cursor to iterate over all entries in the store
        const cursorRequest = objectStore.openCursor();

        cursorRequest.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            // Check if the key starts with the specified prefix
            if (cursor.key.startsWith(prefix)) {
              cursor.delete(); // Delete the entry
            }
            cursor.continue(); // Move to the next entry
          }
        };

        cursorRequest.onerror = (error) => {
          console.error("Cursor error:", error);
          reject(error);
        };

        transaction.oncomplete = () => {
          resolve();
        };

        transaction.onerror = (error) => {
          console.error("Transaction error:", error);
          reject(error);
        };
      };
    });
  }

  public static async getOrSet(key: string, setData: any = null): Promise<any> {
    try {
      if (setData) { // save data
        DbUtils.PutDbData(key, setData);
        return setData;
      } else {// get data
        return await DbUtils.GetDbData(key);
      }
    } catch (e) {
      Utils.HandleException(e, false);
      return setData;
    }
  }

  public static async wp_load_tag_for_filter(userId: number, setData: any = null): Promise<any> {
    const key = `wltff_${userId}`;
    return DbUtils.getOrSet(key, setData);
  }

  public static async wp_load_post_title(wpMode: WpModes, setData: any = null): Promise<any> {
    const key = 'wp_load_post_title' + (wpMode == WpModes.Task ? "_t" : ""); // if workplace, then use old key because old key already in-used
    return DbUtils.getOrSet(key, setData);
  }

  public static async wp_load_tag_for_toolbox(setData: any = null): Promise<any> {
    const key = `wltft`;
    return DbUtils.getOrSet(key, setData);
  }

  public static async wp_load_post_conver(postId : string, setData: any = null): Promise<any> {
    const key = `wlpc_${postId}`;
    return DbUtils.getOrSet(key, setData);
  }

  public static async wp_load_task_conver(taskId : string, setData: any = null): Promise<any> {
    const key = `tkpc_${taskId}`;
    return DbUtils.getOrSet(key, setData);
  }

  public static async wp_load_post_info(postId : string, setData: any = null): Promise<any> {
    const key = `wlpi_${postId}`;
    const rs = await DbUtils.getOrSet(key, setData);
    WpPostInfoMem.set(WpModes.Workplace, postId, rs);
    return rs;
  }

  public static async wp_load_task_info(taskId : string, setData: any = null): Promise<any> {
    const key = `tkpi_${taskId}`;
    const rs = await DbUtils.getOrSet(key, setData);
    WpPostInfoMem.set(WpModes.Task, taskId, rs);
    return rs;
  }

  public static async wp_save_post_info(postInfo: any): Promise<any> {
    if (!postInfo?.postId) return;
    return postInfo.wpMode == WpModes.Task
      ? DbUtils.wp_load_task_info(postInfo.postId, postInfo)
      : DbUtils.wp_load_post_info(postInfo.postId, postInfo);
  }




}
export { DbUtils };